<script lang="ts" setup name="SailingScheduleTradeLaneCollapsible">
import { computed, onMounted, ref } from 'vue';

import { sendSailingScheduleCustomisedEvent } from '~/features/useAnalytics';
import { useShowModal } from '~/features/useModalsProvider';
import { getTradeDeskEmail } from '~/features/useSailingSchedule';
import type { SailingScheduleDateType } from '~/features/useSailingScheduleFilters';
import { useService } from '~/features/useService.js';

import type { HTTP } from '~/services/HTTP';

import BaseCollapsible from '~/components/BaseCollapsible.vue';
import Icon from '~/components/Icon.vue';
import InfoTooltip from '~/components/InfoTooltip.vue';
import SailingScheduleTradeLane from '~/components/SailingScheduleTradeLane.vue';
import SailingScheduleTradelaneButton from '~/components/SailingScheduleTradelaneButton.vue';
import SailingScheduleTradeLaneSettingsModal from '~/components/SailingScheduleTradeLaneSettingsModal.vue';
import SailingScheduleTradeLaneVoyages from '~/components/SailingScheduleTradeLaneVoyages.vue';

import type {
  BaseTagContrastType,
  ITradeLanePort,
  ITradeLaneVessel,
  SailingScheduleTradeLane as TradeLane,
  SailingScheduleV2Response,
} from '~/types';

import SailingScheduleTradeLaneActions from './SailingScheduleTradeLaneActions.vue';

const { tradeLane, voyageService, openByDefaultTrade, showFullScreen } =
  defineProps<{
    tradeLane: TradeLane;
    tagVariant: BaseTagContrastType;
    voyageService: HTTP;
    dateType: SailingScheduleDateType;
    color: string;
    showFullScreen?: boolean;
    openByDefaultTrade?: string;
    downloadService?: HTTP;
  }>();

const showModal = useShowModal();

const dataLoaded = ref(false);
const originalVessels = ref<ITradeLaneVessel[]>([]);
const vessels = ref<ITradeLaneVessel[]>([]);
const ports = ref<ITradeLanePort[]>([]);

const settingsApplied = computed(
  () => JSON.stringify(vessels.value) !== JSON.stringify(originalVessels.value),
);

const filterVisible = <T extends { visible?: boolean }>(items: T[]): T[] =>
  items.filter((item) => item.visible);

const serviceState = useService(
  voyageService,
  ({ data }: SailingScheduleV2Response) => {
    const vesselsData = showFullScreen
      ? data.vessels
      : filterVisible(data.vessels);
    const portsData = showFullScreen ? data.ports : filterVisible(data.ports);

    originalVessels.value = [...vesselsData];
    vessels.value = vesselsData;
    ports.value = portsData;
    return true;
  },
  { preventFetch: true, state: 'IDLE' },
);

const onOpenSettingsModal = async () => {
  sendSailingScheduleCustomisedEvent();

  const result = await showModal(SailingScheduleTradeLaneSettingsModal, {
    options: vessels.value.map((vessel) => ({
      id: vessel.id,
      label: `${vessel.vessel} ${vessel.vvn || ''}`,
      visible: vessel.visible,
    })),
    title: 'Customise Voyages',
    placeholder: 'Search by Vessel Name',
    noResultsMessage:
      'Please clear the search field to see all available vessels.',
  });

  if (result.action === 'SAVE') {
    const updatedVessels: ITradeLaneVessel[] = result.payload
      .map((vessel) => {
        const originalVessel = vessels.value.find((x) => x.id === vessel.id);
        if (!originalVessel) return null;

        return {
          ...originalVessel,
          visible: vessel.visible,
        };
      })
      .filter(Boolean) as ITradeLaneVessel[];
    onUpdated(updatedVessels);
  }
};

const onUpdated = (updatedVessels: ITradeLaneVessel[]) => {
  vessels.value = updatedVessels;
};

const onReset = () => {
  vessels.value = [...originalVessels.value];
};

const showVoyages = () => {
  if (dataLoaded.value) return;
  dataLoaded.value = true;
  serviceState.fetchData();
};
const openByDefault = openByDefaultTrade === tradeLane.tradelane;

onMounted(() => {
  if (openByDefault) {
    showVoyages();
  }
});
</script>

<template>
  <div class="w-full rounded drop-shadow">
    <BaseCollapsible @open="showVoyages" :open-by-default="openByDefault">
      <template #trigger="{ toggle, isOpen }">
        <SailingScheduleTradeLane
          v-bind="tradeLane"
          :color="color"
          :is-open="isOpen"
          :tagVariant="tagVariant"
          :class="{
            'rounded hover:drop-shadow-2': !isOpen,
            'rounded-b-none rounded-t': isOpen,
          }"
          @click="toggle"
        >
          <template #actions>
            <SailingScheduleTradeLaneActions :idle="serviceState.stateIsIdle">
              <button
                v-tooltip.bottom.min="'Customise List'"
                type="button"
                data-testid="settings-button"
                class="grid h-08 w-08 place-items-center rounded-md outline-none"
                :class="{
                  'bg-primary-8 text-white hover:bg-ocean-5': settingsApplied,
                  'text-primary-8 hover:bg-charcoal-2': !settingsApplied,
                }"
                @click.stop="onOpenSettingsModal"
              >
                <Icon icon="g2o:tune-rounded" size="3xl" />
              </button>

              <SailingScheduleTradelaneButton
                :dateType="dateType"
                :downloadService="downloadService"
                :trade-lane="tradeLane.tradelane"
                :tagVariant="tagVariant"
                :vessels="vessels"
                :ports="ports"
              />

              <InfoTooltip
                title="Trade Desk email"
                :description="getTradeDeskEmail(tradeLane.tradeEmail)"
              />

              <button
                v-if="settingsApplied"
                type="button"
                class="h-08 rounded-md px-2 text-xs font-bold uppercase tracking-widest text-red-6 hover:bg-charcoal-1"
                @click.stop="onReset"
              >
                Reset List
              </button>
            </SailingScheduleTradeLaneActions>
          </template>
        </SailingScheduleTradeLane>
      </template>

      <SailingScheduleTradeLaneVoyages
        :service-state="serviceState"
        :vessels="vessels"
        :ports="ports"
        :dateType="dateType"
        :showFullScreen="showFullScreen"
      />
    </BaseCollapsible>
  </div>
</template>
